// ═══════════════════════════════════════════════════════════════════════════
// Mis ventas — la pantalla del vendedor
//
// Sustituye a Cuentas, Historial y la mitad de Mi cuenta, PARA ÉL. El dueño y
// la gerencia conservan las tres, por decisión del operador: quien administra
// necesita cruzar clientes y perseguir cobranza; quien vende necesita su lista.
//
// La decisión de diseño, en una línea: no es una página con todo apilado, es
// UNA LISTA CON LENTES. Lo que antes eran tres pestañas ahora es un control de
// agrupado, porque nunca fueron tres cosas — eran tres ordenamientos.
//
// Qué se ve y qué se filtra lo decide `dominio/mis-ventas`, que es puro y está
// probado. Aquí solo se dibuja.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { estadosDe, resumenVentas, filtrarPorLente, buscar, agrupar, LENTES }
  from "../dominio/mis-ventas.js";
import { COMISION } from "../dominio/estado-comision.js";
import { esperaFolio } from "../datos/remisiones-pg.js";
import { fmt } from "../pdf/comunes.js";
import { Card, SectionTitle } from "./basicos.jsx";
import { inputStyle } from "./estilos.js";
import { useRemisionAcciones } from "./RemisionAcciones.jsx";

const AGRUPADOS = [
  { id: "fecha",   etiqueta: "Por fecha" },
  { id: "cliente", etiqueta: "Por cliente" },
  { id: "estado",  etiqueta: "Por estado" },
];

export function MisVentas({ clients, products, invoices, setInvoices,
                            membresia, onAbrirEnEditor }) {
  const [lente, setLente] = useState("todas");
  const [por, setPor] = useState("fecha");
  const [texto, setTexto] = useState("");
  const [abierta, setAbierta] = useState(null);

  const { barraDe, modales } = useRemisionAcciones({
    clients, products, invoices, setInvoices, membresia, onAbrirEnEditor });

  const resumen = useMemo(() => resumenVentas(invoices), [invoices]);
  const grupos = useMemo(
    () => agrupar(buscar(filtrarPorLente(invoices, lente), texto), por),
    [invoices, lente, texto, por]);

  const cuantas = grupos.reduce((n, g) => n + g.invoices.length, 0);

  return (
    <div>
      <SectionTitle sub={`${invoices.length} remisiones tuyas`}>Mis ventas</SectionTitle>

      {/* ── La respuesta corta, y filtra al tocarla ──────────────────────── */}
      <div style={{ display: "grid", gap: 10, marginBottom: 16,
                    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
        <Numero etiqueta="Me deben los clientes" valor={fmt(resumen.porCobrar)}
          color="#f0a050" activo={lente === "por_cobrar"}
          ayuda="Lo que falta que te paguen. Es del cliente, no tuyo."
          onClick={() => setLente(l => l === "por_cobrar" ? "todas" : "por_cobrar")} />
        <Numero etiqueta="Mi comisión, por acreditarse"
          valor={fmt(resumen.comisionPorAcreditarse)}
          color="#a8d060" activo={lente === "por_acreditarse"}
          ayuda="El cliente ya pagó y a ti todavía no. Es lo tuyo."
          onClick={() => setLente(l => l === "por_acreditarse" ? "todas" : "por_acreditarse")} />
        <Numero etiqueta="Falta timbrar" valor={resumen.porFacturar}
          color="#facc15" activo={lente === "por_facturar"}
          ayuda="Remisiones de clientes con RFC que todavía no tienen CFDI."
          onClick={() => setLente(l => l === "por_facturar" ? "todas" : "por_facturar")} />
      </div>

      {/* ── Los controles ───────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input value={texto} onChange={e => setTexto(e.target.value)}
            placeholder="Buscar por folio o cliente…"
            style={{ ...inputStyle, flex: 1, minWidth: 180 }} />

          <div style={{ display: "flex", gap: 4 }}>
            {AGRUPADOS.map(a => (
              <button key={a.id} onClick={() => setPor(a.id)}
                style={{ fontSize: 11, padding: "7px 12px", borderRadius: 6,
                         cursor: "pointer", fontFamily: "inherit",
                         background: por === a.id ? "#1a1810" : "transparent",
                         border: `1px solid ${por === a.id ? "#3a3222" : "#222"}`,
                         color: por === a.id ? "#c9a96e" : "#777" }}>
                {a.etiqueta}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          {Object.values(LENTES).map(l => (
            <button key={l.clave} onClick={() => setLente(l.clave)}
              style={{ fontSize: 10, padding: "4px 10px", borderRadius: 12,
                       cursor: "pointer", fontFamily: "inherit",
                       background: lente === l.clave ? "#1a1810" : "transparent",
                       border: `1px solid ${lente === l.clave ? "#3a3222" : "#1e1e1e"}`,
                       color: lente === l.clave ? "#c9a96e" : "#666" }}>
              {l.etiqueta}
            </button>
          ))}
        </div>
      </Card>

      {cuantas === 0 && (
        <Card><div style={{ fontSize: 13, color: "#666" }}>
          {invoices.length === 0
            ? "Todavía no tienes remisiones."
            : "Nada con ese filtro. Prueba con «Todas»."}
        </div></Card>
      )}

      {grupos.map(g => (
        <div key={g.clave} style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10,
                        padding: "0 4px 6px", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#c9a96e", textTransform: "capitalize" }}>
              {g.titulo}
            </span>
            <span style={{ fontSize: 11, color: "#555" }}>
              {g.invoices.length} remisi{g.invoices.length === 1 ? "ón" : "ones"} · {fmt(g.total)}
            </span>
            {g.saldo > 0 && (
              <span style={{ fontSize: 11, color: "#f0a050" }}>
                deben {fmt(g.saldo)}
              </span>
            )}
          </div>

          {g.invoices.map(inv => {
            const e = estadosDe(inv);
            const abiertaEsta = abierta === inv.id;
            return (
              <Card key={inv.id}
                style={{ marginBottom: 6, padding: "12px 14px", cursor: "pointer",
                         opacity: e.cancelada ? 0.55 : 1 }}
                onClick={() => setAbierta(abiertaEsta ? null : inv.id)}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline",
                              flexWrap: "wrap" }}>
                  <span style={{ color: "#c9a96e", fontWeight: 600, minWidth: 52 }}>
                    {esperaFolio(inv) ? "s/f" : `#${inv.folio}`}
                  </span>
                  <span style={{ fontSize: 14, flex: 1, minWidth: 120,
                                 textDecoration: e.cancelada ? "line-through" : "none" }}>
                    {inv.client?.name}
                  </span>

                  {/* LOS TRES ESTADOS JUNTOS. Es lo que no existía en ninguna
                      pantalla y por lo que había que visitar las tres. */}
                  <Sello texto={e.fiscal.etiqueta} color={e.fiscal.color} />
                  <Sello texto={e.cancelada ? "—" : e.cobranza.etiqueta}
                    color={e.cobranza.color}
                    extra={e.saldo > 0 ? `deben ${fmt(e.saldo)}`
                         : e.aFavor > 0 ? `sobran ${fmt(e.aFavor)}` : null} />
                  {e.comision.clave !== COMISION.sin_comision.clave && (
                    <Sello texto={e.comision.etiqueta} color={e.comision.color}
                      extra={inv.comision ? fmt(inv.comision) : null} />
                  )}

                  <span style={{ fontSize: 14, color: "#c9a96e", fontWeight: 600,
                                 minWidth: 92, textAlign: "right" }}>
                    {fmt(inv.total)}
                  </span>
                  <span style={{ fontSize: 11, color: "#555", minWidth: 74,
                                 textAlign: "right" }}>
                    {inv.date ? new Date(inv.date).toLocaleDateString("es-MX") : "sin fecha"}
                  </span>
                </div>

                {abiertaEsta && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1e1e1e" }}
                    onClick={ev => ev.stopPropagation()}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11,
                                    marginBottom: 12 }}>
                      <tbody>
                        {(inv.lines || []).map((li, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #141414" }}>
                            <td style={{ padding: "4px 2px", color: "#888" }}>{li.productName}</td>
                            <td style={{ padding: "4px 2px", color: "#666", textAlign: "right",
                                         width: 46 }}>{li.qty}</td>
                            <td style={{ padding: "4px 2px", color: "#666", textAlign: "right",
                                         width: 90 }}>
                              {fmt(li.amount || li.unitPrice * li.qty)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {e.pagado > 0 && (
                      <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
                        Pagado {fmt(e.pagado)} de {fmt(inv.total)}
                      </div>
                    )}
                    {barraDe(inv)}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ))}

      {modales}
    </div>
  );
}

function Numero({ etiqueta, valor, color, activo, ayuda, onClick }) {
  return (
    <button type="button" onClick={onClick} title={ayuda}
      style={{ textAlign: "left", padding: "12px 14px", borderRadius: 10, cursor: "pointer",
               fontFamily: "inherit",
               background: activo ? "#1a1810" : "#141414",
               border: `1px solid ${activo ? "#3a3222" : "#1e1e1e"}` }}>
      <span style={{ display: "block", fontSize: 10, letterSpacing: 1,
                     color: activo ? "#c9a96e" : "#666", marginBottom: 4 }}>
        {etiqueta}
      </span>
      <span style={{ display: "block", fontSize: 20, fontWeight: 600, color }}>{valor}</span>
    </button>
  );
}

/** Un estado, dicho con la palabra que se usa al hablar y no con un icono. */
function Sello({ texto, color, extra }) {
  return (
    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, letterSpacing: 1,
                   background: "#111", color, border: `1px solid ${color}33`,
                   whiteSpace: "nowrap" }}>
      {String(texto).toUpperCase()}{extra ? ` · ${extra}` : ""}
    </span>
  );
}
